import {
  FaActionTracker,
  StructuredLogger,
} from '@brighthub/structured-logging';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { CARE_PLAN_ACCESS } from '../../care-plan-access.token';
import { CARE_PLAN_CLIENT_CONTEXT } from '../../care-plan-client-context.token';
import { CARE_PLAN_NAVI } from '../../care-plan-navi.token';
import { CarePlanEditorRowComponent } from '../../shared/care-plan-editor-row/care-plan-editor-row.component';
import { CarePlanEditorShellComponent } from '../../shared/care-plan-editor-shell/care-plan-editor-shell.component';
import { optionApiErrorMessage } from '../api/option-api-error';
import {
  CarePlanOption,
  CarePlanOptionStatusCode,
  CarePlanOptionTitle,
  CarePlanOptionTypeCode,
  CreateOptionValue,
  KNOWN_OPTION_STATUS,
  UpdateOptionValue,
} from '../options.model';
import { CarePlanOptionsStore } from '../options.store';

export interface OptionEditorDialogData {
  mode: 'add' | 'edit';
  store: CarePlanOptionsStore;
  option?: CarePlanOption;
  initialStatusCode?: CarePlanOptionStatusCode;
}

@Component({
  selector: 'bn-option-editor-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    AutoCompleteModule,
    Button,
    ConfirmDialog,
    CarePlanEditorRowComponent,
    CarePlanEditorShellComponent,
    Select,
    Textarea,
    ToggleSwitch,
  ],
  providers: [ConfirmationService],
  templateUrl: './option-editor-dialog.component.html',
  styleUrl: './option-editor-dialog.component.scss',
})
export class OptionEditorDialogComponent {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _messages = inject(MessageService, { optional: true });
  private readonly _navi = inject(CARE_PLAN_NAVI);
  private readonly _access = inject(CARE_PLAN_ACCESS, { optional: true });
  private readonly _clientContext = inject(CARE_PLAN_CLIENT_CONTEXT, {
    optional: true,
  });
  private readonly _data = this._config.data as OptionEditorDialogData;
  private readonly _persistedOption = signal<CarePlanOption | null>(
    this._data.option ?? null,
  );

  protected readonly store = this._data.store;
  protected readonly mode = this._data.mode;
  protected readonly optionTypes = this.store.optionTypes;
  protected readonly allowCustomTitle =
    this.store.capabilities.allowCustomTitle;

  readonly titleValue = signal<CarePlanOptionTitle | string | null>(
    this._data.option?.title ?? null,
  );
  readonly suggestions = signal<CarePlanOptionTitle[]>([]);
  readonly description = signal(this._data.option?.description ?? '');
  readonly optionTypeCode = signal<CarePlanOptionTypeCode | null>(
    this._data.option?.optionTypeCode ?? null,
  );
  readonly statusCode = signal<CarePlanOptionStatusCode>(
    this._data.initialStatusCode ??
      this._data.option?.statusCode ??
      KNOWN_OPTION_STATUS.DISCUSSED,
  );
  readonly submitAttempted = signal(false);
  readonly saving = signal(false);

  readonly typeLocked = computed(
    () => this.mode === 'edit' || !!this._persistedOption(),
  );
  readonly statusOptions = computed(() =>
    this.store.statusOptionsFor(
      this._persistedOption()?.statusCode ?? KNOWN_OPTION_STATUS.DISCUSSED,
    ),
  );
  private readonly _qualificationRequired = computed(
    () =>
      this.statusCode() === KNOWN_OPTION_STATUS.SELECTED &&
      this._persistedOption()?.statusCode !== KNOWN_OPTION_STATUS.SELECTED &&
      this._requiresQualification(),
  );
  readonly overrideQualification = linkedSignal({
    source: () => `${this._title(this.titleValue())}\u0000${this.statusCode()}`,
    computation: () => false,
  });

  // Read runtime permissions on each use, including immediately before saving.
  canOverrideQualification(): boolean {
    return (
      this._qualificationRequired() &&
      this._access?.canEdit() === true &&
      this._access?.canOverrideOptionQualification?.() === true
    );
  }

  requiresNaviQualification(): boolean {
    return (
      this._qualificationRequired() &&
      !(this.overrideQualification() && this.canOverrideQualification())
    );
  }

  submitLabel(): string {
    return this.requiresNaviQualification() ? 'Send to Navi' : 'Save';
  }

  readonly titleError = computed(() => {
    if (!this.submitAttempted()) return null;
    const title = this._title(this.titleValue());
    if (!title) return 'Enter an option title.';
    if (title.length > 120)
      return 'Keep the option title under 120 characters.';
    return null;
  });

  readonly descriptionError = computed(() => {
    if (!this.submitAttempted() || this.description().length <= 500)
      return null;
    return 'Keep the description under 500 characters.';
  });

  readonly typeError = computed(() =>
    this.submitAttempted() && !this.optionTypeCode()
      ? 'Choose an option type.'
      : null,
  );

  protected readonly dirty = computed(() => {
    const option = this._persistedOption();
    if (!option) {
      return (
        !!this._title(this.titleValue()) ||
        !!this.description().trim() ||
        !!this.optionTypeCode() ||
        this.statusCode() !== KNOWN_OPTION_STATUS.DISCUSSED
      );
    }
    return (
      this._detailsDirty(option) || this.statusCode() !== option.statusCode
    );
  });

  searchTitles(event: { query: string }): void {
    this.suggestions.set(
      this.mode === 'add' && event.query.trim().length >= 1
        ? this.store.searchTitles(event.query.trim())
        : [],
    );
  }

  onTitleSelect(event: AutoCompleteSelectEvent): void {
    const selected = event.value as CarePlanOptionTitle;
    this.titleValue.set(selected);
    this.optionTypeCode.set(selected.optionTypeCode);
  }

  onTitleChange(value: CarePlanOptionTitle | string | null): void {
    const previousValue = this.titleValue();
    this.titleValue.set(value);
    if (!value || (typeof value === 'string' && value.trim().length < 1)) {
      this.suggestions.set([]);
    }
    if (typeof value === 'object' && value) {
      this.optionTypeCode.set(value.optionTypeCode);
    } else if (typeof previousValue === 'object' && previousValue) {
      this.optionTypeCode.set(null);
    }
  }

  async submit(): Promise<void> {
    if (this.saving()) return;
    // An open dialog must not bypass read-only access if permissions change.
    if (this._access && !this._access.canEdit()) return;
    this.submitAttempted.set(true);
    if (this.titleError() || this.descriptionError() || this.typeError())
      return;

    this.saving.set(true);
    try {
      if (this.requiresNaviQualification()) {
        await this._saveAndAskNavi();
      } else if (this._persistedOption()) {
        if (this.dirty()) {
          const option = this._persistedOption();
          if (option) {
            this._persistedOption.set(
              await this.store.update(
                option.id,
                this._updateValue(),
                ...(this.overrideQualification() &&
                this.canOverrideQualification()
                  ? ([true] as const)
                  : []),
              ),
            );
          }
        }
      } else {
        await this.store.create(
          this._createValue(this.statusCode()),
          ...(this.overrideQualification() && this.canOverrideQualification()
            ? ([true] as const)
            : []),
        );
      }
      this._ref.close(true);
    } catch (error) {
      const message = optionApiErrorMessage(
        error,
        "Couldn't save — try again.",
      );
      this._messages?.add({
        severity: 'error',
        summary:
          this.mode === 'edit'
            ? "Couldn't update option"
            : "Couldn't create option",
        detail: message,
      });
    } finally {
      this.saving.set(false);
    }
  }

  tryClose(): void {
    if (!this.dirty()) {
      this._ref.close();
      return;
    }
    this._confirmation.confirm({
      message:
        'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this._ref.close(),
    });
  }

  private _createValue(
    statusCode?: CarePlanOptionStatusCode,
  ): CreateOptionValue {
    const optionTypeCode = this.optionTypeCode();
    if (!optionTypeCode) throw new Error('Choose an option type.');
    return {
      title: this._title(this.titleValue()),
      description: this.description().trim() || undefined,
      optionTypeCode,
      ...(statusCode ? { statusCode } : {}),
    };
  }

  private _updateValue(): UpdateOptionValue {
    const option = this._persistedOption();
    if (!option) return {};
    const title = this._title(this.titleValue());
    const description = this.description().trim();
    const statusCode = this.statusCode();
    return {
      ...(title !== option.title ? { title } : {}),
      ...(description !== (option.description ?? '') ? { description } : {}),
      ...(statusCode !== option.statusCode ? { statusCode } : {}),
    };
  }

  private _detailsUpdateValue(): UpdateOptionValue {
    const value = this._updateValue();
    delete value.statusCode;
    return value;
  }

  private _detailsDirty(option: CarePlanOption): boolean {
    return (
      this._title(this.titleValue()) !== option.title ||
      this.description().trim() !== (option.description ?? '')
    );
  }

  private async _saveAndAskNavi(): Promise<void> {
    let qualificationOption = this._persistedOption();
    if (qualificationOption) {
      const details = this._detailsUpdateValue();
      if (Object.keys(details).length) {
        qualificationOption = await this.store.update(
          qualificationOption.id,
          details,
        );
        this._persistedOption.set(qualificationOption);
      }
    } else {
      qualificationOption = await this.store.create(
        this._createValue(KNOWN_OPTION_STATUS.DISCUSSED),
      );
      this._persistedOption.set(qualificationOption);
    }

    await this._actionLogs.run(
      'care_plan.option.qualify',
      `${this._clientContext?.activeGuid()}:${qualificationOption.id}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this._clientContext?.activeGuid(),
          case_id: qualificationOption.caseId,
          option_id: qualificationOption.id,
        };
        const prompt = this._qualificationPrompt(
          qualificationOption.id,
          qualificationOption.caseId,
        );
        logAttempt.stage = 'qualification';
        logAttempt.localRejection = false;
        const sent =
          !!prompt &&
          (await this._navi.sendMessageToSelectedConversation(
            prompt,
            this._clientContext?.activeGuid() ?? undefined,
          ));
        if (!sent) {
          throw new Error(
            'Navi is unavailable for this conversation. Try again when the chat is ready.',
          );
        }
        logAttempt.accepted = true;
      },
    );
  }

  private _title(value: CarePlanOptionTitle | string | null): string {
    return typeof value === 'string'
      ? value.trim()
      : (value?.title.trim() ?? '');
  }

  private _requiresQualification(): boolean {
    const value = this.titleValue();
    return typeof value === 'object' && value
      ? value.requiresQualification
      : this.store.requiresQualification(this._title(value));
  }

  private _qualificationPrompt(
    optionId: string,
    caseId: string,
  ): string | null {
    const value = this.titleValue();
    if (typeof value !== 'object' || !value) {
      return this.store.qualificationPrompt(
        this._title(value),
        optionId,
        caseId,
      );
    }
    if (!value.requiresQualification) return null;
    return (
      value.qualificationPrompt ||
      `Call the option activation skill and reference Option Id ${optionId}. The client wants to pursue this option to support their Case Id ${caseId}.`
    );
  }
}
